
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Session } from '@supabase/supabase-js';

export const useAuthSession = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const isUpgrading = new URLSearchParams(location.search).get('upgrade') === 'true';
  
  useEffect(() => {
    // Handle access_token in URL (email verification flow)
    const handleEmailVerification = async () => {
      // If we have an access token in the URL
      if (location.hash && location.hash.includes('access_token')) {
        try {
          setIsLoading(true);
          // The hash contains parameters including the access token
          // Supabase will automatically handle this with getSession
          const { data, error } = await supabase.auth.getSession();
          
          if (error) {
            console.error('Error processing verification token:', error);
          } else if (data?.session) {
            setSession(data.session);
            // Clear the hash from URL and redirect to home
            window.history.replaceState(null, '', window.location.pathname);
            navigate('/', { replace: true });
          }
        } catch (error) {
          console.error('Failed to process verification:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    // Run the verification handler
    handleEmailVerification();

    // Regular session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setIsLoading(false);
      if (session && !isUpgrading && !location.hash.includes('access_token')) {
        navigate('/');
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session && !isUpgrading && !location.hash.includes('access_token')) {
        navigate('/');
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate, isUpgrading, location]);

  return {
    session,
    isUpgrading,
    isLoading
  };
};
